import { Center, Loader } from "@mantine/core";
import { type ReactNode, Suspense, lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { hasPermission } from "./api/client";
import { useAuth } from "./auth/AuthProvider";
import { Layout } from "./components/Layout";
import { AuditPage } from "./pages/AuditPage";
import { DashboardPage } from "./pages/DashboardPage";
import { FactoriesPage } from "./pages/FactoriesPage";
import { GatewaysPage } from "./pages/GatewaysPage";
import { LoginPage } from "./pages/LoginPage";
import { MachinesPage } from "./pages/MachinesPage";
import { PlatformPage } from "./pages/PlatformPage";
import { UsersPage } from "./pages/UsersPage";
import { KpiSettingsPage } from "./pages/config/KpiSettingsPage";
import { MachineTypesPage } from "./pages/config/MachineTypesPage";
import { ProductsPage } from "./pages/config/ProductsPage";
import { ReasonCodesPage } from "./pages/config/ReasonCodesPage";
import { ShiftsPage } from "./pages/config/ShiftsPage";

// ECharts is large: load the machine detail page (and the charting library) on demand.
const MachineDetailPage = lazy(() => import("./pages/MachineDetailPage").then((m) => ({ default: m.MachineDetailPage })));

function Guard({ permission, children }: { permission?: string; children: ReactNode }) {
  const { me } = useAuth();
  if (permission && !hasPermission(me, permission)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function App() {
  const { me, ready } = useAuth();
  if (!ready)
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  if (!me) return <LoginPage />;
  const tenantUser = me.company_id !== null;
  return (
    <Layout>
      <Routes>
        {tenantUser ? (
          <>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/machines" element={<Guard permission="machine:read"><MachinesPage /></Guard>} />
            <Route path="/machines/:id" element={<Guard permission="machine:read"><Suspense fallback={<Loader />}><MachineDetailPage /></Suspense></Guard>} />
            <Route path="/factories" element={<Guard permission="factory:read"><FactoriesPage /></Guard>} />
            <Route path="/gateways" element={<Guard permission="gateway:read"><GatewaysPage /></Guard>} />
            <Route path="/users" element={<Guard permission="user:read"><UsersPage /></Guard>} />
            <Route path="/audit" element={<Guard permission="audit:read"><AuditPage /></Guard>} />
            <Route path="/config/machine-types" element={<Guard permission="machine:read"><MachineTypesPage /></Guard>} />
            <Route path="/config/shifts" element={<Guard permission="factory:read"><ShiftsPage /></Guard>} />
            <Route path="/config/products" element={<Guard permission="production:read"><ProductsPage /></Guard>} />
            <Route path="/config/reasons" element={<Guard permission="downtime:read"><ReasonCodesPage /></Guard>} />
            <Route path="/config/kpi" element={<Guard permission="production:read"><KpiSettingsPage /></Guard>} />
          </>
        ) : (
          <Route path="/" element={<PlatformPage />} />
        )}
        {me.is_platform_admin && <Route path="/platform" element={<PlatformPage />} />}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}
